// circle-color.ts — SINGLE SOURCE OF TRUTH for Circle-of-Magic display colour.
//
// Shared by the rune-icon engine (spell sigils / cards, which imports CIRCLE_COLOR from here)
// and the XZ spellsight glow layer (which imports circleColor()). Keeping the palette in ONE
// module is what lets the spellsight glow match the runic spell identity without copying hues.
//
// Keyed by circle NAME; CIRCLE_ID_TO_NAME bridges a bare catalog UUID (e.g. the server's
// computed.spellsight_signature.circle_id) to that name so callers can pass either form.

export const CIRCLE_COLOR: Record<string, string> = {
  'Circle of Abjuration': '#3b82f6', 'Circle of Charm': '#ee82ee', 'Circle of Conjuration': '#eab308',
  'Circle of Divination': '#e2e8f0', 'Circle of Evocation': '#ff0000', 'Circle of Illusion': '#c71585',
  'Circle of Necromancy': '#22c55e', 'Circle of Transmutation': '#ffa500',
};

// SpellCircle::LIST item_id -> item_name (includes/additions.php). Stable catalog UUIDs.
export const CIRCLE_ID_TO_NAME: Record<string, string> = {
  'DD1EFCA2-2A4C-AD3D-B814-A325B2D22D85': 'Circle of Abjuration',
  '54058786-9840-7A02-90F6-36151FA0A3F2': 'Circle of Charm',
  '9931FE30-8ACF-40E2-8E69-EB78925364DF': 'Circle of Conjuration',
  '5886822F-FA79-E5CA-C255-DCC7D375CB52': 'Circle of Divination',
  'A28D2D20-BCE9-1CE0-4371-7F42231E9C43': 'Circle of Evocation',
  'EC1F7FF4-9E4C-DAE0-633A-B0BE7F4A2788': 'Circle of Illusion',
  'F5D2429C-6BA8-901F-2E23-30C780107C47': 'Circle of Necromancy',
  'CC4C52BD-A341-0CAC-6C0B-671C9CC1C2EC': 'Circle of Transmutation',
};

// Neutral "untyped magic" glow — a magical object with no identifiable circle (e.g. a
// passive-only enchantment). White/silver.
export const UNTYPED_MAGIC_COLOR = '#e8eefc';

// Resolve a circle reference (display NAME or catalog UUID, any case) to its glow colour.
// null / unknown -> the untyped-magic neutral.
export function circleColor(circleRef: string | null | undefined): string {
  if (!circleRef) return UNTYPED_MAGIC_COLOR;
  const direct = CIRCLE_COLOR[circleRef];
  if (direct) return direct;
  const name = CIRCLE_ID_TO_NAME[circleRef] ?? CIRCLE_ID_TO_NAME[circleRef.toUpperCase()];
  if (name) {
    const byName = CIRCLE_COLOR[name];
    if (byName) return byName;
  }
  return UNTYPED_MAGIC_COLOR;
}
